import dayjs from "dayjs";
import svLocale from "dayjs/locale/sv";
import { Calendar } from "frost-ui-react";
import { Calendar as CalendarIcon } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { DATE_TIME_FORMAT_TYPES } from "../../../configs/constants/DateTimeFormatTypes";
import { appTheme } from "../../../theme/Theme";
import { FormLabel } from "../AppText/AppText";

export interface DayPickerNewProps {
	value?: Date;
	onChange?: (date?: Date) => void;
	/** i18n key */
	label?: string;
	/** i18n key, rendered under the field */
	description?: string;
	/** i18n key, shown when no date is selected */
	placeholder?: string;
	disabled?: boolean;
	/** Days before this date are not pickable. */
	minDate?: Date;
	containerClassName?: string;
	triggerClassName?: string;
	/** Merged onto the calendar popover — e.g. a z-index override when the
	 * picker sits inside a dialog/sheet (their overlays are z-[1200], floating
	 * content is z-[1300], above them, so this is rarely needed). */
	popoverClassName?: string;
}

/**
 * Tailwind/shadcn replacement for the styled-components {@link DayPicker}.
 *
 * Same behaviour and the same underlying `frost-ui-react` calendar — the
 * difference is that the popover is Radix (so it portals and escapes
 * overflow/stacking contexts, which the old absolutely-positioned one did not)
 * and customisation is via `className` props rather than `margin`/`width`.
 *
 * Kept beside the legacy component rather than replacing it: `DayPicker` still
 * has call sites elsewhere, and this follows the repo's `XNew.tsx` convention.
 */
const DayPickerNew: React.FC<DayPickerNewProps> = ({
	value,
	onChange,
	label,
	description,
	placeholder,
	disabled,
	minDate,
	containerClassName,
	triggerClassName,
	popoverClassName,
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	function handleChange(date?: Date) {
		onChange?.(date);
		// Brief delay so the selection is visible before the popover closes
		setTimeout(() => setOpen(false), 150);
	}

	return (
		<div className={cn("flex flex-col", containerClassName)}>
			{label && <FormLabel>{t(label as any)}</FormLabel>}

			<Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
				<PopoverTrigger asChild disabled={disabled}>
					<button
						type="button"
						className={cn(
							"flex w-full items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm",
							"hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40",
							disabled && "cursor-default bg-gray-50 text-gray-400 hover:border-gray-300",
							triggerClassName
						)}
					>
						<CalendarIcon size={16} className="shrink-0 text-gray-500" />

						<span className={cn(!value && "text-gray-400")}>
							{value
								? dayjs(value).format(DATE_TIME_FORMAT_TYPES.DATE)
								: placeholder && t(placeholder as any)}
						</span>
					</button>
				</PopoverTrigger>

				<PopoverContent align="start" className={cn("w-[300px] p-2", popoverClassName)}>
					<Calendar
						locale={svLocale}
						onChange={handleChange}
						value={value}
						disableDatesBefore={minDate}
						styles={{
							dayFontSize: 14,
							circleFontSizeRatio: 2,
							headerFontSize: 14,
							weekDayFontSize: 11,
							arrowSize: 13,
							highlightStyles: {
								today: { backgroundColor: "white", borderColor: "gray" },
								value: {
									backgroundColor: appTheme.colors.COLOR_ACTION_BLUE,
									color: "white",
								},
							},
						}}
					/>
				</PopoverContent>
			</Popover>

			{description && (
				<p className="mt-1.5 text-xs leading-snug text-gray-500">{t(description as any)}</p>
			)}
		</div>
	);
};

export default DayPickerNew;
